
import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  Alert,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Toast from '../components/Toast';

export default function DeleteAccountScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const { user, session, signOut } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('error');

  const handleBack = () => {
    router.back();
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'error') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const hideToast = () => {
    setToastVisible(false);
  };

  const handleDeleteAccount = async () => {
    console.log('🗑️ handleDeleteAccount called');

    // PRD §5.10 — `account_delete_requested` (`step=first_screen`). The
    // destructive Alert.alert that gates this call IS the user's
    // confirmed intent — fire here so we capture EVERY user who
    // confirmed deletion, even when the API call below fails (e.g.
    // missing token, server 500). The success/`account_deleted` event
    // lives on the confirmation screen.
    import('@/utils/clarityEvents')
      .then(({ logAccountDeleteRequested }) =>
        logAccountDeleteRequested({ step: 'first_screen' })
      )
      .catch(() => {});

    if (!user) {
      console.log('❌ No user found');
      showToast(t('account.deleteAccount.errorUserNotFound'));
      return;
    }

    console.log('🗑️ User found, starting deletion for:', user.id);
    setIsDeleting(true);

    try {
      // Try multiple sources for the access token
      let accessToken = session?.access_token;
      console.log('🗑️ Session from AuthContext:', accessToken ? 'available' : 'not available');
      
      // Fallback: Try to get from AsyncStorage cache if context session is null
      if (!accessToken) {
        console.log('🗑️ Trying AsyncStorage fallback...');
        try {
          const cachedData = await AsyncStorage.getItem('cached_session_data');
          if (cachedData) {
            const parsed = JSON.parse(cachedData);
            accessToken = parsed.session?.access_token;
            console.log('🗑️ AsyncStorage fallback:', accessToken ? 'found' : 'not found');
          }
        } catch (e) {
          console.log('🗑️ AsyncStorage error:', e);
        }
      }
      
      if (!accessToken) {
        console.log('❌ No access token from any source');
        showToast(t('account.deleteAccount.errorSessionNotFound'));
        setIsDeleting(false);
        return;
      }

      console.log('🗑️ Calling backend delete account API...');
      
      // Call backend API to delete account from both profiles and auth.users
      const response = await fetch('https://mobile.padrinho.app/api/auth/delete-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        console.error('❌ Delete account API failed:', data);
        showToast(data.error || t('account.deleteAccount.errorGeneric'));
        setIsDeleting(false);
        return;
      }

      console.log('✅ Account deleted successfully via API');

      // Show success message
      showToast(t('account.deleteAccount.successMessage'), 'success');
      
      // Clear ALL cached data to ensure clean logout
      console.log('🧹 Clearing all session and cache data...');
      try {
        await AsyncStorage.multiRemove([
          'cached_session_data',
          'cached_session_timestamp', 
          'padrinho-elo-auth',
          'supabase.auth.token'
        ]);
        console.log('✅ Cleared all cached session data');
      } catch (e) {
        console.log('⚠️ Could not clear cached session:', e);
      }
      
      // Use AuthContext signOut with timeout (clears React state properly)
      console.log('🔄 Signing out via AuthContext...');
      try {
        const signOutPromise = signOut();
        const timeoutPromise = new Promise<void>((resolve) => setTimeout(resolve, 2000));
        await Promise.race([signOutPromise, timeoutPromise]);
        console.log('✅ SignOut completed or timed out');
      } catch (e) {
        console.log('⚠️ SignOut error (ignored):', e);
      }
      
      // PRD §5.11 — `account_deleted`. The success path of this screen
      // is the canonical site (the confirmation screen previously held
      // this fire, but the production flow now navigates straight to
      // `/welcome` after a successful delete and never re-enters the
      // confirmation screen). Fire BEFORE navigation so the event lands
      // on the still-mounted screen.
      import('@/utils/clarityEvents')
        .then(({ logAccountDeleted }) => logAccountDeleted())
        .catch(() => {});

      // Now redirect - state should be cleared
      console.log('🔄 Redirecting to welcome page...');
      router.replace('/welcome');

    } catch (error) {
      console.error('❌ Error deleting account:', error);
      showToast(t('account.deleteAccount.errorGeneric'));
      setIsDeleting(false);
    }
  };

  const confirmDeletion = () => {
    Alert.alert(
      t('account.deleteAccount.confirmTitle'),
      t('account.deleteAccount.confirmMessage'),
      [
        {
          text: t('account.deleteAccount.confirmCancel'),
          style: 'cancel',
        },
        {
          text: t('account.deleteAccount.confirmDelete'),
          style: 'destructive',
          onPress: handleDeleteAccount,
        },
      ]
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={handleBack} style={styles.backButton}>
          <Svg width="25" height="24" viewBox="0 0 25 24" fill="none">
            <Path d="M16.5859 19L9.58594 12L16.5859 5" stroke="#151515" strokeWidth="2" strokeLinecap="square" />
          </Svg>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('account.deleteAccount.title')}</Text>
        <View style={styles.placeholder} />
      </View>

      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* Warning Icon */}
        <View style={styles.iconContainer}>
          <View style={styles.warningIcon}>
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18A2 2 0 003.64 21H20.36A2 2 0 0022.18 18L13.71 3.86A2 2 0 0010.29 3.86Z" stroke="#DE5609" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
          </View>
        </View>

        {/* Title */}
        <Text style={styles.title}>{t('account.deleteAccount.question')}</Text>

        {/* Warning Message */}
        <View style={styles.warningContainer}>
          <Text style={styles.warningTitle}>{t('account.deleteAccount.warningTitle')}</Text>
          
          <View style={styles.bulletList}>
            <View style={styles.bulletItem}>
              <View style={styles.bullet} />
              <Text style={styles.bulletText}>{t('account.deleteAccount.warningItem1')}</Text>
            </View>
            
            <View style={styles.bulletItem}>
              <View style={styles.bullet} />
              <Text style={styles.bulletText}>{t('account.deleteAccount.warningItem2')}</Text>
            </View>
            
            <View style={styles.bulletItem}>
              <View style={styles.bullet} />
              <Text style={styles.bulletText}>{t('account.deleteAccount.warningItem3')}</Text>
            </View>
            
            <View style={styles.bulletItem}>
              <View style={styles.bullet} />
              <Text style={styles.bulletText}>{t('account.deleteAccount.warningItem4')}</Text>
            </View>
            
            <View style={styles.bulletItem}>
              <View style={styles.bullet} />
              <Text style={styles.bulletText}>{t('account.deleteAccount.warningItem5')}</Text>
            </View>
          </View>

          <Text style={styles.warningFooter}>
            {t('account.deleteAccount.warningFooter')}
          </Text>
        </View>

        {/* Alternative */}
        <View style={styles.alternativeContainer}>
          <Text style={styles.alternativeTitle}>{t('account.deleteAccount.alternativeTitle')}</Text>
          <Text style={styles.alternativeText}>
            {t('account.deleteAccount.alternativeText')}
          </Text>
        </View>
      </ScrollView>

      {/* Bottom Buttons */}
      <View style={styles.buttonContainer}>
        <TouchableOpacity
          style={styles.cancelButton}
          onPress={handleBack}
        >
          <Text style={styles.cancelButtonText}>{t('account.deleteAccount.cancelButton')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.deleteButton, isDeleting && styles.deleteButtonDisabled]}
          onPress={confirmDeletion}
          disabled={isDeleting}
        >
          <Text style={styles.deleteButtonText}>
            {isDeleting ? t('account.deleteAccount.deleteButtonLoading') : t('account.deleteAccount.deleteButton')}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Toast */}
      <Toast
        visible={toastVisible}
        type={toastType}
        message={toastMessage}
        onHide={hideToast}
        duration={4000}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F0EEE4',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
  },
  placeholder: {
    width: 24,
  },
  scrollView: {
    flex: 1,
  },
  iconContainer: {
    alignItems: 'center',
    paddingTop: 40,
    paddingBottom: 24,
  },
  warningIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#FEF3F2',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    textAlign: 'center',
    marginBottom: 32,
    paddingHorizontal: 24,
    lineHeight: 28.8,
  },
  warningContainer: {
    paddingHorizontal: 24,
    marginBottom: 32,
  },
  warningTitle: {
    fontSize: 16,
    fontFamily: 'Inter_600SemiBold',
    color: '#151515',
    marginBottom: 16,
  },
  bulletList: {
    marginBottom: 24,
  },
  bulletItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  bullet: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#DE5609',
    marginTop: 6,
    marginRight: 12,
  },
  bulletText: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#475467',
    lineHeight: 20,
  },
  warningFooter: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#DE5609',
    lineHeight: 20,
    textAlign: 'center',
  },
  alternativeContainer: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    backgroundColor: '#F9FAFB',
    marginHorizontal: 24,
    marginBottom: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E7EAED',
  },
  alternativeTitle: {
    fontSize: 16,
    fontFamily: 'Inter_600SemiBold',
    color: '#151515',
    marginBottom: 8,
  },
  alternativeText: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#475467',
    lineHeight: 20,
  },
  buttonContainer: {
    flexDirection: 'row',
    paddingHorizontal: 24,
    paddingVertical: 24,
    borderTopWidth: 1,
    borderTopColor: '#F0EEE4',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    height: 48,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#BDC2C8',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
  },
  deleteButton: {
    flex: 1,
    height: 48,
    borderRadius: 12,
    backgroundColor: '#DE5609',
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteButtonDisabled: {
    opacity: 0.6,
  },
  deleteButtonText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#FFFFFF',
  },
});
